import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Move, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for relocating a single uploaded file from one slot to another.
 * Handles both required→required, required→additional, additional→required
 * and additional→additional moves. Nothing is re-uploaded — server just
 * shuffles the file entry inside the app doc.
 *
 * Props:
 *   open, onOpenChange
 *   applicationId, authToken
 *   file: { filename, stored_filename }
 *   fromBucket: 'required' | 'additional'
 *   fromSlot: source slot name (string)
 *   requiredDocs: [{name}]   — for target picker
 *   additionalDocs: [{name}] — for target picker
 *   onMoved: () => void      — parent refresh
 */
const MoveFileDialog = ({
  open, onOpenChange,
  applicationId, authToken,
  file, fromBucket, fromSlot,
  requiredDocs = [], additionalDocs = [],
  onMoved,
}) => {
  const [choice, setChoice] = useState('');    // encoded target: "req:<name>" | "add:<name>" | "add:__new__"
  const [customName, setCustomName] = useState('');
  const [moving, setMoving] = useState(false);

  const options = useMemo(() => {
    const opts = [];
    (requiredDocs || []).forEach(d => {
      // Don't offer the same source slot as a target
      if (fromBucket === 'required' && d.name === fromSlot) return;
      opts.push({ value: `req:${d.name}`, label: `Required · ${d.name}`, isDefault: false });
    });
    (additionalDocs || []).forEach(d => {
      if (fromBucket === 'additional' && d.name === fromSlot) return;
      opts.push({ value: `add:${d.name}`, label: `Additional Documents · ${d.name}`, isDefault: false });
    });
    opts.push({ value: 'add:__new__', label: 'Additional Documents · (new label)', isDefault: false });
    return opts;
  }, [requiredDocs, additionalDocs, fromBucket, fromSlot]);

  const reset = () => { setChoice(''); setCustomName(''); };

  const handleMove = async () => {
    if (!choice) {
      toast.error('Pick where to move this file.');
      return;
    }
    let toBucket, toSlot;
    if (choice.startsWith('req:')) {
      toBucket = 'required'; toSlot = choice.slice(4);
    } else if (choice === 'add:__new__') {
      toBucket = 'additional';
      toSlot = customName.trim();
      if (!toSlot) {
        toast.error('Enter a label for the new Additional Documents slot.');
        return;
      }
    } else if (choice.startsWith('add:')) {
      toBucket = 'additional'; toSlot = choice.slice(4);
    }

    setMoving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/applications/${applicationId}/documents/move-file`,
        {
          from_bucket: fromBucket,
          from_slot: fromSlot,
          stored_filename: file.stored_filename,
          to_bucket: toBucket,
          to_slot: toSlot,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`Moved "${file.filename}" to ${toSlot}`);
      onOpenChange(false);
      reset();
      onMoved && onMoved();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Move failed';
      toast.error(typeof detail === 'string' ? detail : 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg" data-testid="move-file-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="h-5 w-5 text-slate-700" />
            Move file to a different slot
          </DialogTitle>
          <DialogDescription>
            <span className="block text-slate-700 truncate"><span className="font-semibold">{file?.filename}</span></span>
            <span className="block text-xs text-slate-500 mt-1">
              Currently in <span className="font-semibold">{fromBucket === 'required' ? 'Required · ' : 'Additional Documents · '}{fromSlot}</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Move to</Label>
            <select
              value={choice}
              onChange={e => setChoice(e.target.value)}
              className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
              data-testid="move-file-target-select"
            >
              <option value="">— Pick a destination —</option>
              {options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {choice === 'add:__new__' && (
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600">New Additional Documents label</Label>
              <Input
                value={customName}
                onChange={e => setCustomName(e.target.value)}
                placeholder='e.g. "Driver License – Paul", "Lease Amendment #3"'
                className="h-9"
                data-testid="move-file-new-label"
                autoFocus
              />
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} disabled={moving} data-testid="move-file-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={moving || !choice}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="move-file-submit"
          >
            {moving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Moving…</> : <><Move className="h-4 w-4 mr-2" /> Move file</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveFileDialog;
